// frontend/packages/business-console/src/pages/Scheduling/SettingsTab.jsx
//
// The two business-level scheduling toggles (doc 20 sec 4.5): crmSync
// (auto | manual) and defaultBookingMode (instant | request). Written through
// the shared whitelist endpoint PATCH /businesses/settings (validated fields
// only). Pattern mirrors AnalyticsAlertsSettings: patch + refreshBusiness +
// 403 handling + a success Snackbar. businessadmin-only server-side; we render
// the controls for everyone and surface a 403 if a non-admin tries to save
// (F.10, no client-side role gating).

import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Typography,
} from '@mui/material';

import { BusinessContext } from '../../contexts/BusinessContext';
import {
  getScheduling,
  saveScheduling,
} from '../../services/schedulingService';
import { Surface, useTokens } from '../../components/primitives';

export default function SettingsTab() {
  const t = useTokens();
  const { business, loading, refreshBusiness } = useContext(BusinessContext);

  const scheduling = useMemo(() => getScheduling(business), [business]);
  // Schema defaults: crmSync manual, defaultBookingMode request (matches the
  // backend appointment-type default when no business default is set).
  const crmSync = scheduling.crmSync === 'auto' ? 'auto' : 'manual';
  const defaultBookingMode =
    scheduling.defaultBookingMode === 'instant' ? 'instant' : 'request';

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  const patch = async (payload, successMsg) => {
    setSaving(true);
    setError(null);
    try {
      await saveScheduling(payload);
      await refreshBusiness();
      if (successMsg) setSavedMsg(successMsg);
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      setError(
        status === 403
          ? serverMsg || 'Only business admins can change scheduling settings.'
          : serverMsg || 'Could not save. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading && !business) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, maxWidth: 720 }}>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Surface sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          New bookings and your CRM
        </Typography>
        <Typography variant="body2" sx={{ color: t.muted, mt: 0.5, mb: 1.5 }}>
          Choose whether a new booking adds the customer to your CRM right away,
          or lands as an inbox lead you promote later.
        </Typography>
        <FormControl disabled={saving}>
          <RadioGroup
            value={crmSync}
            onChange={(e) =>
              patch(
                { crmSync: e.target.value },
                'Booking to CRM setting saved.'
              )
            }
          >
            <FormControlLabel
              value="auto"
              control={<Radio />}
              label={
                <SettingLabel
                  title="Add to CRM automatically"
                  desc="Every booking creates or links a CRM customer immediately."
                />
              }
            />
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label={
                <SettingLabel
                  title="Keep as an inbox lead"
                  desc="The booker stays a lead until you add them to the CRM. The booking is still real."
                />
              }
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Default booking mode
        </Typography>
        <Typography variant="body2" sx={{ color: t.muted, mt: 0.5, mb: 1.5 }}>
          The starting mode for a new service. You can override it per service.
        </Typography>
        <FormControl disabled={saving}>
          <RadioGroup
            value={defaultBookingMode}
            onChange={(e) =>
              patch(
                { defaultBookingMode: e.target.value },
                'Default booking mode saved.'
              )
            }
          >
            <FormControlLabel
              value="instant"
              control={<Radio />}
              label={
                <SettingLabel
                  title="Instant book"
                  desc="Customers book an open slot and it is confirmed automatically."
                />
              }
            />
            <FormControlLabel
              value="request"
              control={<Radio />}
              label={
                <SettingLabel
                  title="Request to book"
                  desc="Requests wait in your Approvals queue until you confirm or decline."
                />
              }
            />
          </RadioGroup>
        </FormControl>

        {saving ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Saving...
            </Typography>
          </Box>
        ) : null}
      </Surface>

      <Snackbar
        open={Boolean(savedMsg)}
        autoHideDuration={3000}
        onClose={() => setSavedMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSavedMsg(null)}
        >
          {savedMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function SettingLabel({ title, desc }) {
  return (
    <Box sx={{ py: 0.25 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {desc}
      </Typography>
    </Box>
  );
}
